import { X } from 'lucide-react-native';
import type { PropsWithChildren, ReactNode } from 'react';
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useEffect, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import {
  displayNameForComment,
  type CommentReplyTarget,
} from './comment-threading';

export const COMMENT_MODAL_COMPACT_BREAKPOINT = 700;
const COMMENT_COMPOSER_MODAL_EDGE_PADDING = 10;

export function CommentModalShell({
  children,
  closeAccessibilityLabel = 'Close comment composer',
  closeDisabled = false,
  closeOnBackdropPress = true,
  compact,
  bottomContent,
  onClose,
  title = 'Comment',
  topContent,
  visible,
}: PropsWithChildren<{
  closeAccessibilityLabel?: string;
  closeDisabled?: boolean;
  closeOnBackdropPress?: boolean;
  compact: boolean;
  bottomContent?: ReactNode;
  onClose: () => void;
  title?: string;
  topContent: ReactNode;
  visible: boolean;
}>) {
  const insets = useSafeAreaInsets();
  const sheetProgressRef = useRef(new Animated.Value(0));
  const sheetProgress = sheetProgressRef.current;

  useEffect(() => {
    if (!visible) {
      sheetProgress.setValue(0);
      return;
    }

    Animated.timing(sheetProgress, {
      duration: 210,
      easing: Easing.out(Easing.cubic),
      toValue: 1,
      useNativeDriver: true,
    }).start();
  }, [sheetProgress, visible]);

  const sheetMotionStyle = {
    opacity: sheetProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.86, 1],
    }),
    transform: [
      {
        translateY: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [compact ? 34 : 26, 0],
        }),
      },
      {
        scale: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [0.985, 1],
        }),
      },
    ],
  };

  const keyboardContent = (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={styles.keyboardView}
    >
      <Animated.View
        style={[
          styles.sheet,
          compact ? styles.sheetCompact : null,
          {
            paddingBottom: insets.bottom,
            paddingTop: compact ? Math.max(insets.top, spacing.sm) : spacing.sm,
          },
          sheetMotionStyle,
        ]}
      >
      <Pressable
        style={styles.sheetPressable}
        onPress={(event) => event.stopPropagation()}
      >
        <View style={styles.header}>
          <Pressable
            accessibilityLabel={closeAccessibilityLabel}
            accessibilityRole="button"
            disabled={closeDisabled}
            onPress={onClose}
            style={styles.closeButton}
          >
            <X color={colors.text} size={24} strokeWidth={2.5} />
          </Pressable>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.headerSpacer} />
        </View>
        <View
          style={[
            styles.topContentFrame,
            styles.topContentInner,
            compact ? styles.topContentInnerCompact : null,
          ]}
        >
          {topContent}
        </View>
        <ScrollView
          contentContainerStyle={[
            styles.threadScrollContent,
            compact ? styles.threadScrollContentCompact : null,
          ]}
          keyboardShouldPersistTaps="handled"
          style={styles.threadScroll}
        >
          {children}
        </ScrollView>
        {bottomContent ? (
          <View
            style={[
              styles.bottomContent,
              compact ? styles.bottomContentCompact : null,
              {
                paddingBottom: COMMENT_COMPOSER_MODAL_EDGE_PADDING,
              },
            ]}
          >
            {bottomContent}
          </View>
        ) : null}
      </Pressable>
      </Animated.View>
    </KeyboardAvoidingView>
  );

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      {closeOnBackdropPress && !closeDisabled ? (
        <Pressable
          style={[styles.backdrop, compact ? styles.backdropCompact : null]}
          onPress={onClose}
        >
          {keyboardContent}
        </Pressable>
      ) : (
        <View
          style={[styles.backdrop, compact ? styles.backdropCompact : null]}
        >
          {keyboardContent}
        </View>
      )}
    </Modal>
  );
}

export function CommentReplyComposerFrame({
  children,
  compact = false,
  onCancel,
  replyTarget,
}: PropsWithChildren<{
  compact?: boolean;
  onCancel: () => void;
  replyTarget: CommentReplyTarget;
}>) {
  return (
    <View
      style={[
        styles.inlineReplyComposer,
        compact ? styles.inlineReplyComposerCompact : null,
      ]}
    >
      <View
        style={[
          styles.replyTargetBanner,
          compact ? styles.replyTargetBannerCompact : null,
        ]}
      >
        <Text style={styles.replyTargetText} numberOfLines={1}>
          Replying to{' '}
          {displayNameForComment({
            authorFullName: replyTarget.replyToFullName,
            authorUid: replyTarget.replyToUid,
            authorUsername: replyTarget.replyToUsername,
            id: replyTarget.parentCommentId,
          })}
        </Text>
        <Pressable
          accessibilityLabel="Cancel reply"
          accessibilityRole="button"
          onPress={onCancel}
          style={styles.replyTargetCancel}
        >
          <X color={colors.mutedText} size={16} strokeWidth={2.4} />
        </Pressable>
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.54)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  backdropCompact: {
    padding: spacing.xs,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  bottomContent: {
    backgroundColor: 'transparent',
    elevation: 8,
    paddingHorizontal: COMMENT_COMPOSER_MODAL_EDGE_PADDING,
    paddingTop: spacing.sm,
  },
  bottomContentCompact: {
    paddingHorizontal: COMMENT_COMPOSER_MODAL_EDGE_PADDING,
    paddingTop: spacing.xs,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  headerSpacer: {
    height: 42,
    width: 42,
  },
  inlineReplyComposer: {
    backgroundColor: colors.surfaceTint,
    borderColor: 'rgba(116, 67, 241, 0.18)',
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    marginLeft: spacing.xl,
    padding: spacing.sm,
  },
  inlineReplyComposerCompact: {
    gap: spacing.sm,
    marginLeft: 0,
    padding: spacing.sm,
  },
  keyboardView: {
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  replyTargetBanner: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.accentSoft,
    borderColor: 'rgba(22, 143, 255, 0.24)',
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.xs,
    maxWidth: '100%',
    paddingLeft: spacing.md,
    paddingRight: spacing.xs,
    paddingVertical: spacing.xs,
  },
  replyTargetBannerCompact: {
    alignSelf: 'stretch',
    paddingLeft: spacing.sm,
  },
  replyTargetCancel: {
    alignItems: 'center',
    borderRadius: 999,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  replyTargetText: {
    color: colors.text,
    flexShrink: 1,
    fontSize: 13,
    fontWeight: '800',
  },
  sheet: {
    alignSelf: 'center',
    backgroundColor: colors.surface,
    borderRadius: 24,
    height: '82%',
    maxHeight: '82%',
    maxWidth: 760,
    overflow: 'hidden',
    width: '96%',
  },
  sheetPressable: {
    flex: 1,
  },
  sheetCompact: {
    alignSelf: 'center',
    borderRadius: 24,
    height: '86%',
    maxHeight: '86%',
    maxWidth: 760,
    width: '98%',
  },
  threadScroll: {
    flex: 1,
  },
  threadScrollContent: {
    paddingBottom: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  threadScrollContentCompact: {
    paddingHorizontal: spacing.sm,
  },
  title: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  topContentFrame: {
    borderBottomColor: colors.border,
    borderBottomWidth: 1,
    overflow: 'hidden',
  },
  topContentInner: {
    gap: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  topContentInnerCompact: {
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
});
